import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  QueryKeyMatchers,
  ApiQueryKeys,
  CustomQueryKeys,
} from "../utils/queryKeys";
import { createScopedLogger } from "../utils/devLogger";

/**
 * Hook for managing job-related cache invalidation in React Query
 *
 * This hook provides utilities to invalidate job-related queries when data changes,
 * ensuring UI consistency across all job lists and components.
 *
 * Features:
 * - Precise query key matching using centralized patterns
 * - Development logging for debugging cache invalidation
 * - Type-safe query key generation
 * - Performance monitoring
 */
export const useJobCacheInvalidation = () => {
  const queryClient = useQueryClient();
  const logger = createScopedLogger("JobCacheInvalidation");

  /**
   * Invalidates all job-related queries to ensure UI consistency
   *
   * This function invalidates:
   * - Individual job queries: /firms/{firmId}/jobs/{jobId}
   * - Job lists: /firms/{firmId}/jobs, ['jobs', firmId]
   * - Operations queries: /ops/jobs, /ops/my-jobs
   * - Processing results: /firms/{firmId}/jobs/{jobId}/processing-results, ['jobProcessingResults', jobId]
   *
   * @param options - Optional parameters for logging context
   * @param options.firmId - Firm ID to provide logging context
   * @param options.jobId - Job ID to provide logging context
   */
  const invalidateJobRelatedQueries = useCallback(
    async (options?: { firmId?: string; jobId?: string }) => {
      const startTime = performance.now();
      const { firmId, jobId } = options || {};

      logger.debug("Starting job-related query invalidation", {
        firmId,
        jobId,
      });

      // Track invalidated queries for logging
      const invalidatedKeys: unknown[][] = [];
      let predicateMatchCount = 0;

      // Use precise predicate matching based on centralized patterns
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const isMatch = QueryKeyMatchers.isJobRelated(query.queryKey);
          if (isMatch) {
            predicateMatchCount++;
            invalidatedKeys.push([...query.queryKey]);
            logger.queryKeyMatch(query.queryKey, true, "job-related pattern");
          }
          return isMatch;
        },
        refetchType: "active",
      });

      const endTime = performance.now();

      // Log invalidation summary
      logger.cacheInvalidation("invalidateJobRelatedQueries", {
        queriesInvalidated: predicateMatchCount,
        patterns: ["Comprehensive job-related predicate matches"],
        targetKeys: invalidatedKeys,
        duration: endTime - startTime,
      });

      logger.performance("invalidateJobRelatedQueries", startTime, endTime, {
        firmId,
        jobId,
        totalInvalidated: predicateMatchCount,
      });
    },
    [queryClient, logger],
  );

  /**
   * Invalidates queries for a specific firm
   * Uses precise matching for firm-related query patterns
   *
   * @param firmId - The firm ID to invalidate queries for
   */
  const invalidateFirmQueries = useCallback(
    async (firmId: string) => {
      const startTime = performance.now();
      logger.debug("Starting firm-specific query invalidation", { firmId });

      const invalidatedKeys: unknown[][] = [];
      let predicateMatchCount = 0;

      // Generate centralized query keys
      const customJobsKey = CustomQueryKeys.jobs(firmId);
      const firmKey = ApiQueryKeys.firm(firmId);

      await Promise.all([
        // Legacy custom job queries
        queryClient.invalidateQueries({ queryKey: customJobsKey }),

        // Generated firm queries
        queryClient.invalidateQueries({ queryKey: firmKey }),

        // Firm-specific queries using centralized matcher
        queryClient.invalidateQueries({
          predicate: (query) => {
            const isMatch = QueryKeyMatchers.isFirmRelated(
              query.queryKey,
              firmId,
            );
            if (isMatch) {
              predicateMatchCount++;
              invalidatedKeys.push([...query.queryKey]);
              logger.queryKeyMatch(
                query.queryKey,
                true,
                "firm-related pattern",
              );
            }
            return isMatch;
          },
          refetchType: "active",
        }),
      ]);

      const endTime = performance.now();
      const totalInvalidated = predicateMatchCount + 2; // +2 for the specific keys

      logger.cacheInvalidation("invalidateFirmQueries", {
        queriesInvalidated: totalInvalidated,
        patterns: [
          "Custom job queries",
          "Firm queries",
          "Firm-related predicate matches",
        ],
        targetKeys: [[...customJobsKey], [...firmKey], ...invalidatedKeys],
        duration: endTime - startTime,
      });

      logger.performance("invalidateFirmQueries", startTime, endTime, {
        firmId,
        totalInvalidated,
      });
    },
    [queryClient, logger],
  );

  /**
   * Invalidates all operations-related queries
   * Uses precise pattern matching for ops endpoints
   */
  const invalidateOpsQueries = useCallback(async () => {
    const startTime = performance.now();
    logger.debug("Starting operations query invalidation");

    const invalidatedKeys: unknown[][] = [];
    let matchCount = 0;

    await queryClient.invalidateQueries({
      predicate: (query) => {
        const isMatch = QueryKeyMatchers.isOpsRelated(query.queryKey);
        if (isMatch) {
          matchCount++;
          invalidatedKeys.push([...query.queryKey]);
          logger.queryKeyMatch(query.queryKey, true, "ops-related pattern");
        }
        return isMatch;
      },
      refetchType: "active",
    });

    const endTime = performance.now();

    logger.cacheInvalidation("invalidateOpsQueries", {
      queriesInvalidated: matchCount,
      patterns: ["Operations predicate matches"],
      targetKeys: invalidatedKeys,
      duration: endTime - startTime,
    });

    logger.performance("invalidateOpsQueries", startTime, endTime, {
      totalInvalidated: matchCount,
    });
  }, [queryClient, logger]);

  return {
    invalidateJobRelatedQueries,
    invalidateFirmQueries,
    invalidateOpsQueries,
  };
};
